import React, { FC, useId } from 'react';
import { useTranslation } from 'next-i18next';
import Tooltip from '../Tooltip';
import { PDP_GALLERY_BADGE_OVERLAY_ATTR } from './constants';
import styles from './CanadianBadge.module.scss';

export interface CanadianBadgeProps {
  className?: string;
  'data-testid'?: string;
  variant?: 'madeInCanada' | 'canadianBrand';
}

/**
 * Maple leaf badge for PDP gallery overlays: made in Canada (black) or Canadian-based brand (green).
 * Tooltip uses gallery copy (no PLP links).
 */
const CanadianBadge: FC<CanadianBadgeProps> = ({
  className,
  'data-testid': dataTestId,
  variant = 'madeInCanada',
}) => {
  const { t } = useTranslation('common');
  const tooltipId = useId();
  const tooltipKey =
    variant === 'canadianBrand'
      ? 'tooltip.canadianBrandGallery'
      : 'tooltip.madeInCanadaGallery';
  const ariaLabel =
    variant === 'canadianBrand'
      ? t('product.canadianBrand', 'Canadian based brand')
      : t('product.madeInCanada', 'Made in Canada');

  return (
    // `ActivatableImageContainer` skips open-zoom when the event target is inside this node.
    <div
      className={className}
      data-testid={dataTestId}
      {...{ [PDP_GALLERY_BADGE_OVERLAY_ATTR]: '' }}
    >
      <Tooltip
        id={tooltipId}
        content={
          <div
            dangerouslySetInnerHTML={{
              __html: t(tooltipKey),
            }}
          />
        }
        className={styles.button}
        popupClassName={styles.tooltipBubble}
        contentClassName={styles.tooltipContent}
        side="bottom"
        align="start"
        sideOffset={0}
        showArrow={false}
      >
        <svg
          className={styles.icon}
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          aria-label={ariaLabel}
          role="img"
          data-testid={`${variant}-icon`}
        >
          <path
            d="M16.5995 29.5329L16.2995 23.7796C16.2952 23.685 16.3121 23.5906 16.349 23.5034C16.386 23.4161 16.442 23.3383 16.513 23.2756C16.584 23.213 16.6682 23.167 16.7593 23.1411C16.8504 23.1153 16.9462 23.1102 17.0395 23.1262L22.7662 24.1329L21.9928 21.9996C21.9615 21.9147 21.9575 21.8221 21.9814 21.7348C22.0053 21.6475 22.0559 21.5699 22.1262 21.5129L28.3995 16.4329L26.9862 15.7729C26.8907 15.7278 26.815 15.6494 26.7733 15.5524C26.7315 15.4554 26.7267 15.3466 26.7595 15.2462L27.9995 11.4329L24.3862 12.1996C24.2883 12.22 24.1863 12.2059 24.0976 12.1597C24.0089 12.1136 23.9389 12.0381 23.8995 11.9462L23.1995 10.2996L20.3795 13.3262C20.315 13.3943 20.2302 13.4395 20.1379 13.4553C20.0455 13.471 19.9505 13.4564 19.8671 13.4136C19.7837 13.3708 19.7165 13.3021 19.6754 13.2178C19.6344 13.1336 19.6218 13.0383 19.6395 12.9462L20.9995 5.93291L18.8195 7.19291C18.7683 7.2229 18.7115 7.24207 18.6526 7.24922C18.5937 7.25638 18.534 7.25136 18.4771 7.23448C18.4203 7.21761 18.3675 7.18924 18.322 7.15112C18.2765 7.11301 18.2394 7.06596 18.2128 7.01291L15.9995 2.66624L13.7862 7.01291C13.7596 7.06596 13.7225 7.11301 13.677 7.15112C13.6316 7.18924 13.5788 7.21761 13.5219 7.23448C13.465 7.25136 13.4053 7.25638 13.3464 7.24922C13.2875 7.24207 13.2307 7.2229 13.1795 7.19291L10.9995 5.93291L12.3595 12.9462C12.3772 13.0383 12.3646 13.1336 12.3236 13.2178C12.2825 13.3021 12.2153 13.3708 12.1319 13.4136C12.0486 13.4564 11.9536 13.471 11.8612 13.4553C11.7688 13.4395 11.684 13.3943 11.6195 13.3262L8.79951 10.2996L8.09951 11.9462C8.06008 12.0381 7.99008 12.1136 7.90138 12.1597C7.81268 12.2059 7.71074 12.22 7.61285 12.1996L3.99951 11.4329L5.23951 15.2462C5.27237 15.3466 5.26748 15.4554 5.22574 15.5524C5.184 15.6494 5.1083 15.7278 5.01285 15.7729L3.59951 16.4329L9.87285 21.5129C9.9431 21.5699 9.99374 21.6475 10.0177 21.7348C10.0416 21.8221 10.0376 21.9147 10.0062 21.9996L9.23285 24.1329L14.9595 23.1262C15.0529 23.1102 15.1486 23.1153 15.2397 23.1411C15.3309 23.167 15.415 23.213 15.486 23.2756C15.557 23.3383 15.6131 23.4161 15.65 23.5034C15.687 23.5906 15.7039 23.685 15.6995 23.7796L15.3995 29.5329H16.5995Z"
            className={
              variant === 'canadianBrand' ? styles.iconGreen : styles.iconBlack
            }
          />
        </svg>
      </Tooltip>
    </div>
  );
};

CanadianBadge.displayName = 'CanadianBadge';

export default CanadianBadge;
